import {Sprites} from '../../UIGameImageDictionary.ts'
import {UIGameSpriteHandler} from '../../../UIGameSpriteHandler.ts'
import {SpriteView} from '../../../ui_canvas_engine/SpriteView.ts'
import {TipsPort} from '../../ui_game_tooltip/TipsPort.ts'
import {type UIGameManager} from '../../UIGameManager.ts'
import {type TranslationObject} from '@colonist/i18n'
import {type PortEdge} from '@colonist/game-state-types'
import {UIGameMapPortView} from './UIGameMapPortView.ts'
import {type HexCorner, type HexFace, type Point} from '@colonist/game-mechanic-validators'

export class UIGameMapPortController {
    private readonly uiGameManager: UIGameManager
    private readonly hexCornerToPortViewMap = new Map<HexCorner, UIGameMapPortView>()

    constructor(uiGameManager: UIGameManager) {
        this.uiGameManager = uiGameManager
        this.drawPorts()
    }

    private drawPorts() {
        for(const port of this.uiGameManager.gameState.mapState.portState.portEdges) {
            for(const touchingFace of port.hexEdge.touchingFaces()) {
                const tile = this.uiGameManager.gameState.mapState.tileState.getTileHex(touchingFace)
                const isTileLand = tile != undefined && tile.isTileLand()
                if(isTileLand) continue
                this.drawPort(touchingFace, port)
            }
        }
    }

    private drawPort(hexFace: HexFace, portEdge: PortEdge) {
        if(portEdge.type == undefined) return

        const texture = UIGameSpriteHandler.getPortImage(portEdge.type)
        if(texture == undefined) return

        const center = hexFace.toPixel(this.uiGameManager.mapController.mapView.mapCenter, this.uiGameManager.mapController.mapView.hexagonHeight / 2)

        this.drawPier(portEdge.hexEdge.endPoints()[0].toPixel(this.uiGameManager.mapController.mapView.mapCenter, this.uiGameManager.mapController.mapView.hexagonHeight / 2), center)
        this.drawPier(portEdge.hexEdge.endPoints()[1].toPixel(this.uiGameManager.mapController.mapView.mapCenter, this.uiGameManager.mapController.mapView.hexagonHeight / 2), center)
        const portSize = this.uiGameManager.mapController.mapView.hexagonHeight / 2
        const portX = center.x - portSize / 2 - portSize * 0.1
        const portY = center.y - portSize / 2 - portSize * 0.3
        const port = new UIGameMapPortView(this.uiGameManager, portX, portY, portSize, portSize, this.uiGameManager.mapController.mapView, texture)
        const portTooltipTitle: TranslationObject = TipsPort.title(portEdge.type)
        const portTooltipBody: TranslationObject = TipsPort.body(portEdge.type)
        port.setTip(portTooltipTitle, [portTooltipBody])
        const portCorners = portEdge.hexEdge.endPoints()
        for(const corner of portCorners) {
            this.hexCornerToPortViewMap.set(corner, port)
        }
    }

    private drawPier(p1: Point, p2: Point) {
        const dx = p1.x - p2.x
        const dy = p1.y - p2.y

        const w = this.uiGameManager.mapController.mapView.hexagonHeight * 80 / 100 / 8
        const h = this.uiGameManager.mapController.mapView.hexagonHeight * 240 / 100 / 8
        const x = p1.x - dx / 3 - w / 2
        const y = p1.y - dy / 3 - h / 2
        const pier = new SpriteView(x, y, w, h, this.uiGameManager.mapController.mapView, UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.port_pier))
        pier.zIndex = this.uiGameManager.mapController.mapView.layers.ports
        pier.rotate(UIGameMapPortController.getPierAngle(dx, dy))
    }

    private static getPierAngle(dx: number, dy: number): number {
        const rads = Math.atan2(dy, dx)
        const degrees = rads * 180 / Math.PI
        return degrees - 90
    }

    highlightPort(hexCorner: HexCorner) {
        for(const corner of this.hexCornerToPortViewMap.keys()) {
            if(!hexCorner.sameCorner(corner)) continue
            this.hexCornerToPortViewMap.get(corner)?.highlight()
        }
    }

    removePortHighlight() {
        for(const portView of this.hexCornerToPortViewMap.values()) {
            portView.removeHighlight()
        }
    }
}
